import React, { type ReactElement } from 'react';

import './SliderComponent.css';

/**
 * Props for SliderComponent
 */
interface SliderComponentProps {
  /** Current value of the slider (0-100) */
  value: number;
  /** Callback function triggered when slider value changes */
  onChange?: (value: number) => void;
  /** Disables the slider interaction */
  disabled?: boolean;
  /** Custom color for the progress bar (default: #254B86) */
  progressColor?: string;
  /** Custom color for the unfilled track (default: #ebecf2) */
  trackColor?: string;
  /** Array of level labels to display below slider (e.g., ['Low', 'Medium', 'High']) */
  levels?: string[];
  /** Header text displayed above the slider */
  header?: string;
  /** Subheader text displayed below the header */
  subHeader?: string;
  /** Makes slider read-only (hides thumb, prevents interaction) */
  readOnly?: boolean;
  /** Enables dark mode styling */
  darkMode?: boolean;
  /** Custom aria-label for accessibility (defaults to header or 'Slider') */
  ariaLabel?: string;
  /** Custom height for the slider track (default: '0.75rem') */
  trackHeight?: string;
  /** Enables progressive fill animation from left to right on mount */
  animated?: boolean;
  /** Duration of the fill animation in milliseconds (default: 1000) */
  animationDuration?: number;
}

/**
 * SliderComponent - A customizable range slider with levels, labels, and accessibility features.
 *
 * @component
 *
 * **Mandatory Props:**
 * - `value` (number): Current slider value (0-100)
 *
 * **Optional Props:**
 * - All other props are optional with sensible defaults
 *
 * @example
 * // Basic usage (mandatory prop only)
 * <SliderComponent value={50} />
 *
 * @example
 * // With onChange callback
 * <SliderComponent value={50} onChange={(val) => console.log(val)} />
 *
 * @example
 * // With levels and labels
 * <SliderComponent
 *   value={75}
 *   onChange={setValue}
 *   levels={['Negligible', 'Low', 'Medium', 'High', 'Severe']}
 *   header="Risk Level"
 *   subHeader="Select the risk level"
 * />
 *
 * @example
 * // Read-only mode
 * <SliderComponent value={60} readOnly levels={['Low', 'High']} />
 *
 * @example
 * // Dark mode
 * <SliderComponent value={30} darkMode levels={['Min', 'Max']} />
 *
 * @param {SliderComponentProps} props - Component props
 * @returns {ReactElement} Slider component
 */
function SliderComponent({
  value,
  onChange,
  disabled,
  progressColor,
  trackColor,
  levels,
  header,
  subHeader,
  readOnly,
  darkMode,
  ariaLabel,
  trackHeight,
  animated,
  animationDuration,
}: SliderComponentProps): ReactElement {
  /**
   * Handles slider input change
   */
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && !readOnly && onChange) {
      onChange(Number(e.target.value));
    }
  };

  const progress = progressColor ?? '#254B86';

  /**
   * Calculates the current level index based on slider value
   */
  const getLevelIndex = () => {
    if (!levels || levels.length === 0) {
      return -1;
    }

    const step = 100 / (levels.length - 1);

    return Math.round(value / step);
  };

  const currentLevelIndex = getLevelIndex();

  /**
   * Generates aria-label for accessibility
   * Priority: ariaLabel prop > header prop > 'Slider'
   */
  const getAriaLabel = () => {
    if (ariaLabel) {
      return ariaLabel;
    }

    if (header) {
      return header;
    }

    return 'Slider';
  };

  /**
   * Generates aria-valuetext for accessibility
   * Returns current level label or numeric value
   */
  const getAriaValueText = () => {
    if (levels && levels.length > 0 && currentLevelIndex >= 0) {
      return levels[currentLevelIndex];
    }

    return `${value}`;
  };

  /**
   * Handles click on level labels to jump to that position
   */
  const handleLevelClick = (index: number) => {
    if (!disabled && !readOnly && onChange && levels && levels.length > 0) {
      const step = 100 / (levels.length - 1);
      const newValue = index * step;

      onChange(newValue);
    }
  };

  return (
    <div className={`SliderComponent ${darkMode ? 'dark' : ''}`}>
      {header && <div className='SliderComponent-header'>{header}</div>}
      {subHeader && (
        <div className='SliderComponent-subHeader'>{subHeader}</div>
      )}
      <div className='SliderComponent-track'>
        <input
          type='range'
          min={0}
          max={100}
          step={1}
          value={value}
          onChange={handleChange}
          disabled={disabled}
          className={`SliderComponent-input ${readOnly ? 'readOnly' : ''} ${
            animated ? 'animated' : ''
          }`}
          style={
            {
              '--progress-color': progress,
              '--progress-value': `${value}%`,
              '--track-color': trackColor ?? '#ebecf2',
              '--track-height': trackHeight ?? '0.75rem',
              '--animation-duration': `${animationDuration ?? 1000}ms`,
            } as React.CSSProperties
          }
          aria-label={getAriaLabel()}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={value}
          aria-valuetext={getAriaValueText()}
          aria-readonly={readOnly}
        />
        {levels && levels.length > 0 && (
          <div className='SliderComponent-labels'>
            {levels.map((level) => {
              const index = levels.indexOf(level);
              const step = 100 / (levels.length - 1);
              const position = index * step;
              const isFirst = index === 0;
              const isLast = index === levels.length - 1;

              /**
               * Calculates left position for level label
               * Adds padding for first/last labels when only 2 levels
               */
              const getLeftPosition = () => {
                const padding = levels.length === 2 ? '50px' : '0px';

                if (isFirst) {
                  return padding;
                }

                if (isLast) {
                  return `calc(100% - ${padding})`;
                }

                return `${position}%`;
              };

              /**
               * Calculates transform for level label alignment
               */
              const getTransform = () => {
                if (isFirst) {
                  return 'none';
                }

                if (isLast) {
                  return 'translateX(-100%)';
                }

                return 'translateX(-50%)';
              };

              return (
                <span
                  key={level}
                  role='button'
                  tabIndex={0}
                  className={`SliderComponent-label ${
                    index === currentLevelIndex ? 'active' : ''
                  } ${disabled || readOnly ? 'disabled' : ''}`}
                  onClick={() => handleLevelClick(index)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      handleLevelClick(index);
                    }
                  }}
                  style={{
                    left: getLeftPosition(),
                    transform: getTransform(),
                  }}>
                  {level}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

SliderComponent.defaultProps = {
  onChange: undefined,
  disabled: false,
  progressColor: '#254B86',
  trackColor: '#ebecf2',
  levels: undefined,
  header: undefined,
  subHeader: undefined,
  readOnly: false,
  darkMode: false,
  ariaLabel: undefined,
  trackHeight: '0.75rem',
  animated: false,
  animationDuration: 1000,
};

export default SliderComponent;
